/**
 * DraggableCamera - 可拖拽的摄像头窗口（含实时分析面板）
 *
 * 鼠标/触摸拖拽移动位置，限制在视口内，双击重置
 * 底部附带行为分析面板，宽度与视频一致
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FrameAnalysis } from '../types';

interface DraggableCameraProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isCameraOff: boolean;
  analysis?: FrameAnalysis | null;
}

const VIDEO_W = 384; // w-96

export default function DraggableCamera({
  videoRef,
  isCameraOff,
  analysis,
}: DraggableCameraProps) {
  if (isCameraOff) return null;
  return <CameraWindow videoRef={videoRef} analysis={analysis} />;
}

function CameraWindow({
  videoRef,
  analysis,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  analysis?: FrameAnalysis | null;
}) {
  const elRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(() => ({
    x: typeof window !== 'undefined' ? window.innerWidth - VIDEO_W - 10 : 0,
    y: 10,
  }));
  const dragging = useRef(false);
  const dragStart = useRef({ sx: 0, sy: 0, px: 0, py: 0 });

  const clamp = useCallback((x: number, y: number) => {
    const h = elRef.current?.offsetHeight ?? 350;
    return {
      x: Math.max(0, Math.min(x, window.innerWidth - VIDEO_W)),
      y: Math.max(0, Math.min(y, window.innerHeight - h)),
    };
  }, []);

  const handlePointerDown = useCallback(
    (cx: number, cy: number) => {
      dragging.current = true;
      dragStart.current = { sx: cx, sy: cy, px: pos.x, py: pos.y };
    },
    [pos],
  );

  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!dragging.current) return;
      e.preventDefault();
      const cx = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const cy = 'touches' in e ? e.touches[0].clientY : e.clientY;
      setPos(
        clamp(
          dragStart.current.px + (cx - dragStart.current.sx),
          dragStart.current.py + (cy - dragStart.current.sy),
        ),
      );
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetPos = useCallback(() => {
    setPos({ x: window.innerWidth - VIDEO_W - 10, y: 10 });
  }, []);

  return (
    <div
      ref={elRef}
      className="fixed z-50 select-none cursor-grab active:cursor-grabbing"
      style={{ left: pos.x, top: pos.y, touchAction: 'none' }}
      onMouseDown={(e) => handlePointerDown(e.clientX, e.clientY)}
      onTouchStart={(e) =>
        handlePointerDown(e.touches[0].clientX, e.touches[0].clientY)
      }
      onDoubleClick={resetPos}
    >
      <div
        className="rounded-xl overflow-hidden shadow-2xl shadow-black/40 border border-[#334155]/60 bg-[#1E293B]"
        style={{ width: VIDEO_W }}
      >
        {/* 视频 */}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-72 object-cover"
        />

        {/* 分析面板 */}
        {analysis && <AnalysisPanel analysis={analysis} />}
      </div>
    </div>
  );
}

/** 底部行为分析面板 */
function AnalysisPanel({ analysis }: { analysis: FrameAnalysis }) {
  const { face, posture, confidence, nervousness, engagement } = analysis;

  const expressionLabel =
    face.smileScore > 0.3
      ? '微笑'
      : face.browPosition === 'raised'
        ? '惊讶'
        : face.browPosition === 'furrowed'
          ? '皱眉'
          : '平静';

  const expressionIntensity =
    face.smileScore > 0.3
      ? face.smileScore * 100
      : face.browPosition === 'raised'
        ? Math.min(100, Math.abs(face.browHeight) * 1200)
        : face.browPosition === 'furrowed'
          ? Math.min(100, face.browHeight * 2000)
          : 0;

  const postureLabel = posture.armsCrossed
    ? '抱臂'
    : posture.leanAngle > 10
      ? '前倾'
      : posture.leanAngle < -10
        ? '后仰'
        : '端正';

  return (
    <div className="bg-[#0F1419]/95 backdrop-blur-sm border-t border-[#334155]/50 px-3 py-2.5">
      {/* 三列：自信 / 紧张 / 投入 */}
      <div className="flex items-center gap-3 mb-2.5">
        {(
          [
            {
              label: '自信',
              value: confidence,
              gradient: 'linear-gradient(to right, #F59E0B, #FBBF24)',
            },
            {
              label: '紧张',
              value: 100 - nervousness,
              gradient: 'linear-gradient(to right, #F59E0B, #FBBF24)',
            },
            {
              label: '投入',
              value: engagement,
              gradient: 'linear-gradient(to right, #F59E0B, #FBBF24)',
            },
          ] as const
        ).map(({ label, value, gradient }) => (
          <div key={label} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-[15px] font-bold text-slate-100 tabular-nums">
              {Math.round(value)}
            </span>
            <div className="w-full h-1.5 rounded-full bg-slate-700 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${Math.round(value)}%`, background: gradient }}
              />
            </div>
            <span className="text-[11px] text-slate-500">{label}</span>
          </div>
        ))}
      </div>

      {/* 四列：眼神 / 表情 / 姿态 / 眨眼 */}
      <div className="grid grid-cols-4 gap-2">
        {(
          [
            {
              label: '眼神',
              value: face.eyeContact ? '专注' : '游离',
              bar: face.eyeContact ? 100 : 30,
            },
            {
              label: '表情',
              value: expressionLabel,
              bar: Math.max(8, expressionIntensity),
            },
            {
              label: '姿态',
              value: postureLabel,
              bar: posture.stability * 100,
            },
            {
              label: '眨眼',
              value: `${face.blinkRate}次/分`,
              bar: Math.min(face.blinkRate / 30, 1) * 100,
            },
          ] as const
        ).map(({ label, value, bar }) => (
          <div key={label} className="bg-slate-700/60 rounded-lg px-2 py-1.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-500">{label}</span>
              <span className="text-[12px] font-medium text-slate-200">
                {value}
              </span>
            </div>
            <div className="w-full h-1 rounded-full bg-slate-800 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${Math.round(bar)}%`,
                  background: 'linear-gradient(to right, #F59E0B, #FBBF24)',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* 头部姿态 */}
      <div className="flex items-center justify-center gap-4 mt-2 pt-1.5 border-t border-slate-700">
        {[
          { label: '俯仰', value: analysis.face.headPose.pitch },
          { label: '偏转', value: analysis.face.headPose.yaw },
          { label: '倾斜', value: analysis.face.headPose.roll },
        ].map(({ label, value }) => {
          const normalized = Math.max(-1, Math.min(1, value));
          const pct = ((normalized + 1) / 2) * 100;
          return (
            <div key={label} className="flex items-center gap-1.5">
              <span className="text-nowrap text-ellipsis text-[11px] text-slate-600 w-4 text-right">
                {label}
              </span>
              <div className="w-12 h-1 rounded-full bg-slate-700 overflow-hidden ml-1">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${pct}%`,
                    background: 'linear-gradient(to right, #F59E0B, #FBBF24)',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
